import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl.ts'
import { memo, useCallback, useMemo, useState } from 'react'
import useLibraryGroups from '@/pages/Library/hooks/useLibraryGroups.ts'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import { ITreeGroup } from '@/shared/Tree/tree.types.ts'
import useTreeExpandCtrl from '@/shared/Tree/ctrl/useTreeExpandCtrl.ts'
import { ILibraryGroup } from '@/pages/Library/libraryGroups.types.ts'
import MenuOptionEdit from '@/shared/Dropdowns/DropdownListItem/DropdownListItemEdit'
import MenuOptionDelete from '@/shared/Dropdowns/DropdownListItem/DropdownListItemDelete'
import DefaultTreeGroup from '@/shared/Tree/ui/DefaultTreeGroup'
import libraryGroupsApi from '@/pages/Library/libraryGroups.api.ts'
import libraryEvents from '@/pages/Library/events/library.events.ts'
import css from '@/pages/Parameters/components/ParameterGroupsTree/ParameterTree.module.css'
import Tree from '@/shared/Tree'
import defaultTreeGroupStyling from '@/shared/Tree/ui/DefaultTreeGroup/defaultTreeGroup.styling.ts'
import CreateEntityButton from '@/shared/Buttons/CreateEntityButton'
import CreateLibraryGroupModal from '@/pages/Library/components/Modals/CreateLibraryGroupModal'
import UpdateLibraryGroupModal from '@/pages/Library/components/Modals/UpdateLibraryGroupModal'

interface IProps {
  treeSelectCtrl: ReturnType<typeof useTreeSelectCtrl>
}

const LibraryGroupsTree = (props: IProps) => {
  // Модальные окна
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)
  const [initialCreateGroupModalParentId, setInitialCreateGroupModalParentId] = useState<ILibraryGroup['id']>(null)
  const [editingGroupId, setEditingGroupId] = useState<ILibraryGroup['id']>(null)

  const [isOpenExpand, setIsOpenExpand] = useState(false)
  const [clickedOnExpand, setClickedOnExpand] = useState(false)

  const groups = useLibraryGroups().libraryGroupsState.items
  const handleGroupDelete = useCallback(
    (groupId: ILibraryGroup['id']) => {
      const group = groups.find((g) => g.id === groupId)

      confirmService.requestConfirm({
        buttonType: 'primary',
        description: `Вы действительно хотите удалить группу "${group.name}"?`,
        onConfirm: () =>
          libraryGroupsApi.deleteGroup(group.id).then((r) => {
            if (r.status === 'success') {
              libraryEvents.deletedGroup(r.body.id)
            }
          }),
      })
    },
    [groups]
  )

  const treeGroups: ITreeGroup<ILibraryGroup, number>[] = useMemo(() => {
    if (!groups.length) return []

    return groups.map((g) => ({ id: g.id, parentId: g.parentId, data: g, type: 'folder' }))
  }, [groups])

  const expandCtrl = useTreeExpandCtrl(
    'library-groups-tree',
    isOpenExpand,
    treeGroups,
    [],
    setIsOpenExpand,
    clickedOnExpand,
    setClickedOnExpand
  )

  const renderGroup = (group: ITreeGroup<ILibraryGroup, number>) => {
    const menuContent = (
      <>
        <MenuOptionEdit
          text="Редактировать"
          onClick={() => {
            setEditingGroupId(group.data.id)
          }}
        />
        <MenuOptionDelete text="Удалить" onClick={() => handleGroupDelete(group.data.id)} />
      </>
    )

    return (
      <DefaultTreeGroup
        key={group.id}
        group={group.data}
        title={group.data.name}
        onCreate={() => {
          setCreateGroupModalOpen(true)
          setInitialCreateGroupModalParentId(group.data.id)
        }}
        threeDotsMenuContent={menuContent}
      />
    )
  }

  return (
    <>
      <div className={css.wrapperTree}>
        <div className="flex-lines gap8">
          <span className="text-sm text-description" style={{ marginLeft: '12px' }}>
            Группы
          </span>
          <Tree
            leafs={[]}
            groups={treeGroups}
            renderGroup={renderGroup}
            renderLeaf={null}
            styling={{ groupClassname: defaultTreeGroupStyling.groupClassname }}
            expandCtrl={expandCtrl}
            selectCtrl={props.treeSelectCtrl}
            onClickGroup={(group: Record<string, any>) => props.treeSelectCtrl.handleSelect(group.id)}
          />
        </div>
        <div className={css.btnCreateGroup}>
          <CreateEntityButton
            fullWidth={true}
            type="default"
            onClick={() => setCreateGroupModalOpen(true)}
            text="Создать группу"
          />
        </div>
      </div>
      <CreateLibraryGroupModal
        initialParentId={initialCreateGroupModalParentId}
        open={createGroupModalOpen}
        onClose={() => {
          setInitialCreateGroupModalParentId(null)
          setCreateGroupModalOpen(false)
        }}
      />
      <UpdateLibraryGroupModal
        groupId={editingGroupId}
        open={Boolean(editingGroupId)}
        onClose={() => setEditingGroupId(null)}
      />
    </>
  )
}

export default memo(LibraryGroupsTree)
